import { gql, NetworkStatus, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useApolloClient, useReadQuery } from '@apollo/client/react'
import { ErrorAlert, LocationCard } from '@repo/shared-client/components/blocks'
import { STATIC_MINIMAP_RANGE_KM } from '@repo/shared-client/mapbox'
import { createFileRoute } from '@tanstack/react-router'
import SFPageLoading from '@web/src/components/primatives/sf-page-loading/sf-page-loading'
import { useAuth } from '@web/src/hooks/use-auth'
import { useMapBox } from '@web/src/hooks/use-mapbox'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { env } from '@web/src/lib/env.client'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import ChatMessageFloater from 'src/features/chat/chat-message-floater/chat-message-floater'
import ProfileAboutCard from 'src/features/profile/profile-about-card/profile-about-card'
import ProfileDetailsCard from 'src/features/profile/profile-details-card/profile-details-card'
import ProfileHeader from 'src/features/profile/profile-header/profile-header'
import ProfileMenu from 'src/features/profile/profile-menu/profile-menu'
import ProfilePartnerCard from 'src/features/profile/profile-partner-card/profile-partner-card'
import ProfileTaglineCard from 'src/features/profile/profile-tagline-card/profile-tagline-card'
import ProfileVerification from 'src/features/profile/profile-verification/profile-verification'
import ProfileViews from 'src/features/profile/profile-views/profile-views'
import { PartnerInfo, ProfileInfo, ProfileMediaInfo } from 'src/lib/fragments'
import type { ProfileAboutQuery, ProfileAboutQueryVariables } from 'types/graphql'

const PROFILE_ABOUT_QUERY: TypedDocumentNode<ProfileAboutQuery, ProfileAboutQueryVariables> = gql`
  query ProfileAboutQuery($profileId: String!) {
    isBlocked(profileId: $profileId)
    isFriend(profileId: $profileId) {
      id
      friendId
      approved
    }
    isFollowing(profileId: $profileId) {
      id
      followingId
    }
    friendsCounts(filter: { profileId: $profileId }) {
      friends
      pending
      requests
    }
    profile: profile(id: $profileId) {
      ...ProfileInfo
      partners {
        ...PartnerInfo
        profile {
          ...ProfileInfo
        }
      }
    }
  }
  ${PartnerInfo}
  ${ProfileInfo}
  ${ProfileMediaInfo}
`

export const Route = createFileRoute('/(auth)/(main-layout)/profile/$profileId_/about')({
  component: AboutPage,
  head: () =>
    seo({
      title: `Profile About | ${env.VITE_APP_NAME}`,
      description: 'Profile about page',
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
      meta: [{ name: 'rating', content: 'adult' }],
    }),
  loader: async ({ context, params }) => {
    const { queryRef } = await queryAndPreload(context, PROFILE_ABOUT_QUERY, {
      variables: { profileId: params.profileId },
    })
    return { queryRef }
  },
})

function AboutPage() {
  const { t } = useTranslation()
  const { currentUser, hasPermission } = useAuth()
  const { mapBoxToken } = useMapBox()
  const { queryRef } = Route.useLoaderData()
  const apolloClient = useApolloClient()
  const { data, error, networkStatus } = useReadQuery(queryRef as QueryRef<ProfileAboutQuery>)

  const handleRefetch = async () => {
    await apolloClient.refetchQueries({ include: [PROFILE_ABOUT_QUERY] })
  }

  const isInitialLoading = networkStatus === NetworkStatus.loading

  if (isInitialLoading) {
    return <SFPageLoading />
  }

  if (error) {
    return <ErrorAlert error={error} />
  }

  if (!data?.profile) {
    return <ErrorAlert error={{ name: 'Empty', message: t('Profile Not Found') }} />
  }

  const profile = data.profile
  const canViewMap = hasPermission('location:view-map') && Boolean(mapBoxToken)
  const distanceRaw = profile.distance
  const distanceKm = distanceRaw != null && Number.isFinite(distanceRaw) ? Math.round(distanceRaw) : 0
  const locationDisplayText = t('Near {{location}}', { location: profile.location ?? '' })
  const latitude = profile.locationLat ?? 0
  const longitude = profile.locationLong ?? 0
  const mapRangeKm = canViewMap ? STATIC_MINIMAP_RANGE_KM : undefined
  const mapToken = mapBoxToken ?? ''

  return (
    <div className="mx-auto w-full max-w-7xl px-0">
      <ProfileHeader
        isBlocked={data.isBlocked}
        isFollowing={data.isFollowing}
        isFriend={data.isFriend}
        onQueryRefetch={handleRefetch}
        profile={data.profile}
      />
      <div className="mb-4 grid grid-cols-2 gap-3 px-2 md:grid-cols-4">
        <div className="col-span-2 hidden md:col-span-1 md:mt-6 md:flex md:w-full md:justify-center">
          <ProfileMenu
            className="hidden w-[80%] max-w-full md:flex"
            isBlocked={data.isBlocked}
            onQueryRefetch={handleRefetch}
            profile={data.profile}
          />
        </div>
        <div className="col-span-2 mt-10 min-w-0 md:col-span-2 md:mt-0">
          <div className="flex w-full flex-col items-stretch gap-3">
            <ProfileTaglineCard profile={data.profile} />
            <ProfileDetailsCard className="md:hidden" profile={data.profile} />
            <ProfileAboutCard profile={data.profile} />
            <div className="flex w-full flex-col items-stretch gap-3">
              {data.profile?.partners?.map((partner) => {
                return <ProfilePartnerCard key={partner.id} partner={partner} />
              })}
            </div>
            <div className="flex w-full flex-col items-stretch gap-3 md:hidden">
              <ProfileVerification profileId={data.profile?.id} />

              {(currentUser.profile?.id === data.profile?.id || hasPermission('profile:see-views')) && (
                <ProfileViews profileId={data.profile.id} />
              )}
            </div>

            <LocationCard
              className="md:hidden"
              distanceKm={distanceKm}
              latitude={latitude}
              location={locationDisplayText}
              longitude={longitude}
              mapboxToken={mapToken}
              rangeKm={mapRangeKm}
              showSupporterPlaceholder={!canViewMap}
            />
            {currentUser.profile?.id !== data.profile?.id && !data.isBlocked && (
              <ChatMessageFloater profileId={data.profile?.id} />
            )}
          </div>
        </div>
        <div className="col-span-1 min-w-0">
          <div className="hidden flex-col items-stretch gap-3 md:flex">
            <ProfileDetailsCard profile={data.profile} />
            {(currentUser.profile?.id === data.profile?.id || hasPermission('profile:see-views')) && (
              <ProfileViews profileId={data.profile.id} />
            )}
            <ProfileVerification profileId={data.profile?.id} />
            <LocationCard
              distanceKm={distanceKm}
              latitude={latitude}
              location={locationDisplayText}
              longitude={longitude}
              mapboxToken={mapToken}
              rangeKm={mapRangeKm}
              showSupporterPlaceholder={!canViewMap}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
